"use client"

import React from "react"
import { View, StyleSheet, Pressable } from "react-native"
import { Text, useTheme } from "react-native-paper"
import { ChevronRight, ChevronDown, Folder } from "lucide-react"
import type { FileNode } from "../../../types/files"

interface FileTreeViewProps {
  data: FileNode
  onFolderSelect: (folder: FileNode) => void
  selectedFolder?: FileNode | null
  level?: number
}

export function FileTreeView({ data, onFolderSelect, selectedFolder, level = 0 }: FileTreeViewProps) {
  const theme = useTheme()
  const [expanded, setExpanded] = React.useState(level < 2) // Auto-expand first two levels

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setExpanded(!expanded)
  }

  const isSelected = selectedFolder?.path === data.path

  // Only render if it's a directory
  if (data.type !== "directory") {
    return null
  }

  // Filter children to only include directories
  const directoryChildren = data.children?.filter((child) => child.type === "directory") || []

  return (
    <View>
      <Pressable
        onPress={() => {
          onFolderSelect(data)
          if (directoryChildren.length > 0 && !expanded) {
            setExpanded(true)
          }
        }}
        style={({ pressed }) => [
          styles.item,
          { marginLeft: level * 16 },
          isSelected && { backgroundColor: theme.colors.primaryContainer },
          pressed && { opacity: 0.7 },
        ]}
      >
        <View style={styles.itemContent}>
          {directoryChildren.length > 0 ? (
            expanded ? (
              <ChevronDown size={16} color={theme.colors.onSurface} />
            ) : (
              <ChevronRight size={16} color={theme.colors.onSurface} />
            )
          ) : (
            <View style={styles.emptySpace} />
          )}
          <Folder size={16} color={isSelected ? theme.colors.primary : theme.colors.onSurface} />
          <Text
            style={[
              styles.itemText,
              { color: theme.colors.onSurface },
              isSelected && { color: theme.colors.primary, fontWeight: "500" },
            ]}
            numberOfLines={1}
          >
            {data.name}
          </Text>
        </View>
      </Pressable>

      {expanded && directoryChildren.length > 0 && (
        <View>
          {directoryChildren
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((child, index) => (
              <FileTreeView
                key={`${child.path}-${index}`}
                data={child}
                onFolderSelect={onFolderSelect}
                selectedFolder={selectedFolder}
                level={level + 1}
              />
            ))}
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  item: {
    padding: 6,
    cursor: "pointer",
    borderRadius: 4,
    marginBottom: 2,
  },
  itemContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  itemText: {
    fontSize: 14,
    marginLeft: 4,
    flex: 1,
  },
  emptySpace: {
    width: 16,
    height: 16,
  },
})

